/**
 * Registers a handler for the catalog's `set_theme` action.
 *
 * The catalog owns the schema; this module only implements the
 * handler. Dispatches noki-fe's existing `change_theme` redux action
 * (no new redux surface introduced).
 *
 * The orchestrator's propose/commit gate (server-side) runs around
 * this handler — voice never silently mutates state. The handler
 * itself just dispatches; commit semantics live above it.
 */

import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { change_theme } from "redux/actions/AppActions";

import { useCopilotAction } from "./actionRegistry";

const SET_THEME_DESCRIPTOR = Object.freeze({
  name: "set_theme",
  description: "Implemented by noki-fe (redux change_theme).",
  classification: "WRITE_SAFE",
  confirmStyle: "verbal",
  parameters: [
    { name: "theme", type: "string", description: "light|dark", required: true },
  ],
});

export function useCopilotThemeAction() {
  const dispatch = useDispatch();
  const currentTheme = useSelector((s) => s?.app?.theme ?? "light");
  const descriptor = useMemo(() => SET_THEME_DESCRIPTOR, []);

  useCopilotAction(descriptor, async (args) => {
    // eslint-disable-next-line no-console
    console.info("[copilot] set_theme.invoke", { args, current: currentTheme });
    const requested =
      typeof args?.theme === "string" ? args.theme.toLowerCase().trim() : "";

    if (requested !== "light" && requested !== "dark") {
      // eslint-disable-next-line no-console
      console.warn("[copilot] set_theme.invalid", { args, requested });
      throw new Error(
        `'theme' must be 'light' or 'dark'. Got ${JSON.stringify(args?.theme)}.`,
      );
    }
    if (requested === currentTheme) {
      // eslint-disable-next-line no-console
      console.info("[copilot] set_theme.idempotent", { theme: requested });
      // Idempotent — agent can acknowledge "you're already in dark mode".
      return { theme: requested, changed: false };
    }
    dispatch(change_theme(requested));
    // eslint-disable-next-line no-console
    console.info("[copilot] set_theme.dispatched", { theme: requested });
    return { theme: requested, changed: true };
  });
}
